import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config';
import { Button, Card, CardContent, Typography, Grid, Box, Snackbar, Alert, List, ListItem, ListItemIcon } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { checkAuthAndRedirect } from '../utils/auth';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';

const CheckoutPage = () => {
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [showCancelledMessage, setShowCancelledMessage] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const checkAuth = async () => {
      const isAuthenticated = await checkAuthAndRedirect(navigate);
      if (!isAuthenticated) return;

      const searchParams = new URLSearchParams(location.search);
      if (searchParams.get('canceled') === 'true') {
        setShowCancelledMessage(true);
      }
    };

    checkAuth();
  }, [location, navigate]);

  const plans = [
    { 
      id: 'plan_basic', 
      lookupID: 'base_499',
      name: 'Basic Plan', 
      originalPrice: 4.99, 
      trialDays: 10,
      features: [
        '200 receipts/month',
        'AI line item extraction',
        'AI Categorization of expenses',
        'Detailed spending dashboard insights',
      ]
    },
    { 
      id: 'plan_premium', 
      lookupID: 'Premium-ebd9b1f', 
      name: 'Premium Plan', 
      originalPrice: 14.99, 
      salePrice: 9.99, 
      onSale: true, 
      comingSoon: true,
      features: [
        'More features!',
        'Support our development!',
        'Coming soon!',
      ]
    },
  ];

  const handlePlanSelect = (plan) => {
    if (!plan.comingSoon) {
      setSelectedPlan(plan);
    }
  };

  const handleCheckout = async () => {
    if (!selectedPlan) {
      alert('Please select a plan before checking out.');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_BASE_URL}/api/create-checkout-session`, {
        planID: selectedPlan.id,
        lookupID: selectedPlan.lookupID,
      }, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      // Redirect to Stripe Checkout
      window.location.href = response.data.checkoutUrl;
    } catch (error) {
      console.error('Error creating checkout session:', error);
      alert('An error occurred while creating the checkout session. Please try again.');
    }
  };

  return (
    <div className="checkout-page p-4">
      <Snackbar
        open={showCancelledMessage}
        autoHideDuration={6000}
        onClose={() => setShowCancelledMessage(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setShowCancelledMessage(false)} severity="warning" sx={{ width: '100%' }}>
          Stripe checkout was cancelled. Please try again.
        </Alert>
      </Snackbar>

      <Typography variant="h4" gutterBottom>
        Choose a Plan
      </Typography>
      <Grid container spacing={3}>
        {plans.map((plan) => (
          <Grid item xs={12} sm={6} key={plan.id}>
            <Card 
              onClick={() => handlePlanSelect(plan)}
              raised={selectedPlan && selectedPlan.id === plan.id}
              sx={{
                cursor: plan.comingSoon ? 'not-allowed' : 'pointer',
                opacity: plan.comingSoon ? 0.7 : 1,
                transition: 'all 0.3s ease-in-out',
                backgroundColor: 'background.paper',
                position: 'relative',
                overflow: 'hidden',
                boxShadow: selectedPlan && selectedPlan.id === plan.id 
                  ? '0 0 15px 5px rgba(138, 43, 226, 0.4)' 
                  : 'none',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  borderRadius: 'inherit',
                  padding: '2px',
                  background: 'linear-gradient(to right, #3b82f6, #8a2be2, #10b981)',
                  WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                  WebkitMaskComposite: 'xor',
                  maskComposite: 'exclude',
                  opacity: selectedPlan && selectedPlan.id === plan.id ? 1 : 0,
                  transition: 'opacity 0.3s ease-in-out',
                },
                '&:hover': {
                  boxShadow: plan.comingSoon ? 'none' : '0 0 10px 2px rgba(138, 43, 226, 0.2)',
                },
              }}
            >
              {plan.onSale && !plan.comingSoon && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: '10px',
                    right: '-30px',
                    transform: 'rotate(45deg)',
                    backgroundColor: '#ff4081',
                    color: 'white',
                    padding: '5px 40px',
                    fontWeight: 'bold',
                    zIndex: 1,
                  }}
                >
                  SALE
                </Box>
              )}
              {plan.comingSoon && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: '20px',
                    right: '-65px',
                    transform: 'rotate(45deg)',
                    backgroundColor: '#ffa726',
                    color: 'white',
                    padding: '6px 0',
                    fontWeight: 'bold',
                    fontSize: '0.7rem',
                    zIndex: 1,
                    width: '200px',
                    textAlign: 'center',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                  }}
                >
                  COMING SOON
                </Box>
              )}
              <CardContent>
                <Typography variant="h5" gutterBottom>{plan.name}</Typography>
                <Box mb={2}>
                  {(plan.trialDays > 0) && (
                    <Typography 
                      variant="body1" 
                      component="div" 
                      sx={{ 
                        mb: 1.5,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        textAlign: 'center'
                      }}
                    >
                      <Box 
                        sx={{ 
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px'
                        }}
                      >
                        <Box 
                          component="span" 
                          sx={{ 
                            fontWeight: 800,
                            fontSize: '1.1rem',
                            background: 'linear-gradient(45deg, #ff4081, #8a2be2)',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                          }}
                        >
                          Free 
                        </Box>
                        <Box component="span" sx={{ fontWeight: 600 }}>
                          {plan.trialDays}-day trial
                        </Box>
                      </Box>
                      <Box component="span" sx={{ color: 'text.secondary', fontSize: '0.9rem' }}>
                        Cancel any time
                      </Box>
                    </Typography>
                  )}
                  <div>
                    <Typography 
                      variant="h6" 
                      component="span" 
                      sx={{ 
                        textDecoration: plan.onSale && plan.salePrice ? 'line-through' : 'none', 
                        color: plan.onSale && plan.salePrice ? 'text.secondary' : 'text.primary',
                        ...(!(plan.onSale && plan.salePrice) && {
                          background: 'linear-gradient(45deg, #3b82f6, #8a2be2)',
                          WebkitBackgroundClip: 'text',
                          WebkitTextFillColor: 'transparent',
                          fontWeight: 600
                        })
                      }}
                    >
                      ${plan.originalPrice.toFixed(2)}
                    </Typography>
                    {plan.onSale && plan.salePrice && !plan.comingSoon && (
                      <Typography variant="h6" component="span" sx={{ color: '#ff4081', ml: 1 }}>
                        ${plan.salePrice.toFixed(2)}
                      </Typography>
                    )}
                    <Typography variant="body2" component="span" sx={{ ml: 1 }}>
                      / month
                    </Typography>
                  </div>
                </Box>
                <List dense>
                  {plan.features.map((feature, index) => (
                    <ListItem key={index} disableGutters>
                      <ListItemIcon sx={{ minWidth: '28px' }}>
                        <CheckCircleOutlineIcon 
                          sx={{ 
                            fontSize: 19, 
                            color: '#b388ff',
                            opacity: 1
                          }} 
                        />
                      </ListItemIcon>
                      <Typography variant="body2" sx={{ color: 'text.primary' }}>{feature}</Typography>
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
      <Button
        variant="contained"
        color="primary"
        onClick={handleCheckout}
        disabled={!selectedPlan}
        sx={{ mt: 3 }}
      >
        Next
      </Button>
    </div>
  );
};

export default CheckoutPage;